import { toast } from "react-toastify";

import firestore from "src/apiService/firestore";
import { getUserId } from "src/config/storage";
import {
  userPlanSelector,
  effectiveUserTimeZoneSelector,
  isSupportUserSessionSelector,
  isImpersonationSessionSelector,
} from "src/store/system/selector";
import { getWhatnotIntegrations } from "src/store/plaidIntegration/selector";
import type { WhatnotIntegration } from "src/interfaces/plaidIntegration.interface";
import { uploadFile } from "src/utils/storage";
import { moveSaleApi } from "src/apiService/modules/sales";
import type { Sale } from "src/interfaces/sale.interface";
import {
  getWhatnotUser,
  RequiredExtensionVersion,
} from "src/utils/extension/whatnot";
import {
  whatnotIntegrationCreate,
  whatnotProcessFile,
} from "src/apiService/modules/whatnot";
import { recordEventWithCheck } from "src/apiService/modules/events";
import type MonthYear from "src/interfaces/monthYear.interface";
import {
  endOfMonthInTimeZone,
  startOfMonthInTimeZone,
  zonedDateTimeToUtcDate,
} from "src/utils/timezone";

import type { Dispatcher, SyncStatus, ErrorSyncStatusCode } from "../../types";
import { updateSyncStatus as _updateSyncStatus } from "../actions";
import {
  saveSyncDate,
  getLastSyncDate,
  setBeforeUnloadHandler,
  clearBeforeUnloadHandler,
  ReSyncInterval,
  RunningKeys,
  isUserAllowedToDirectImport,
  shouldRunDailySync,
  checkIfItIsInitialImport,
  checkExtensionOrDispatchError,
  notifySyncAlreadyLocked,
} from "../utils";
import { withSyncLock, type SyncLock } from "../syncLock";

import WhatnotClient, {
  NotLoggedError,
  UnauthorizedError,
} from "./WhatnotClient";

function isRefundAdjustmentLedgerMessage(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.startsWith("reversing payout") ||
    lower.startsWith("reversal of sales transaction for order refund")
  );
}

interface LedgerDedupState {
  seenOrderUuids: Set<string>;
  seenLedgerIds: Set<string>;
}

/**
 * Filter predicate for ledger pagination batches:
 * - first occurrence per ledger node.id (transactions and pagination overlaps)
 * - first SALES/refund ADJUSTMENT per order uuid (sales enrichment)
 */
function keepLedgerTransaction(
  edge: unknown,
  dedup: LedgerDedupState
): boolean {
  const node = ((edge as { node?: Record<string, unknown> }).node ||
    edge) as Record<string, unknown>;
  if (!node || typeof node !== "object") return true;

  const ledgerId = node.id;
  if (ledgerId != null && String(ledgerId)) {
    const id = String(ledgerId);
    if (dedup.seenLedgerIds.has(id)) return false;
    dedup.seenLedgerIds.add(id);
  }

  const isSaleOrRefund =
    node.transactionType === "SALES" ||
    (node.transactionType === "ADJUSTMENT" &&
      isRefundAdjustmentLedgerMessage(String(node.message || "")));

  if (!isSaleOrRefund) return true;

  const uuid = (node.order as { uuid?: string } | undefined)?.uuid;
  if (!uuid || typeof uuid !== "string") return true;

  if (dedup.seenOrderUuids.has(uuid)) return false;

  dedup.seenOrderUuids.add(uuid);
  return true;
}

async function updateIntegration(integrationId: string) {
  try {
    const data = await getWhatnotUser();

    if (data.cookies && data.username) {
      await whatnotIntegrationCreate({
        integrationId,
        cookies: data.cookies,
        username: data.username,
        whatnotUserId: data.userId,
      });
      recordEventWithCheck({
        type: "feature-adoption",
        feature: "integration-added",
        element: "sale-platform-whatnot",
      });
    }
  } catch (err) {
    console.error("There was an error:", err);
  }
}

function updateSyncStatus(status: SyncStatus) {
  return _updateSyncStatus({
    integration: "whatnot" as const,
    status,
  });
}

function setKeepAliveInterval(client, timeout = 60000) {
  let cancel = false;
  let interval: ReturnType<typeof setTimeout> | null = null;

  const f = async () => {
    if (cancel) return;

    try {
      await client.getUsername();
    } catch (e) {}

    if (cancel) return;
    interval = setTimeout(f, timeout);
  };

  f();

  return () => {
    cancel = true;
    if (interval) clearTimeout(interval);
  };
}

async function syncTransactions(
  client: WhatnotClient,
  integration: WhatnotIntegration,
  date: Date,
  earliestInProcessDate: Date | null,
  lock: SyncLock
): Promise<Set<string>> {
  const timestamp = date.valueOf();
  const uid = await getUserId();
  const ledgerDedup: LedgerDedupState = {
    seenOrderUuids: new Set<string>(),
    seenLedgerIds: new Set<string>(),
  };
  const lastDateStr = getLastSyncDate(
    integration,
    "whatnotLastSyncDateTransactions"
  );
  const lastDate = new Date(lastDateStr);

  // Widen fetch window to include in-process sales from earlier dates,
  // with a 1-day buffer for timezone boundary discrepancies
  let fetchFrom = lastDate;
  if (earliestInProcessDate && earliestInProcessDate < lastDate) {
    fetchFrom = new Date(earliestInProcessDate.getTime() - 24 * 60 * 60 * 1000);
  }

  console.log(
    `[Whatnot] lastSyncDate: ${lastDate.toISOString()}, earliestInProcessDate: ${
      earliestInProcessDate?.toISOString() ?? "none"
    }, fetchFrom: ${fetchFrom.toISOString()}`
  );

  let index = 0;

  for await (const transactions of client.getLedgerOverviewFull({
    startTime: fetchFrom.getTime() / 1000,
    endTime: Date.now() / 1000,
  })) {
    lock.throwIfAborted();
    if (!transactions.length) continue;

    // Only upload/process transactions newer than lastDate
    // (fetch window may be widened for in-process resolution)
    const newTransactions = transactions.filter((edge) => {
      const node = ((edge as { node?: Record<string, unknown> }).node ||
        edge) as Record<string, unknown>;
      if (node && typeof node === "object") {
        const txDate = new Date(String(node.statusUpdatedAt || node.createdAt));
        if (txDate < lastDate) return false;
      }
      return keepLedgerTransaction(edge, ledgerDedup);
    });

    if (newTransactions.length) {
      lock.throwIfAborted();
      const currentFilename = `${timestamp}_${integration.id}_${String(
        index++
      ).padStart(2, "0")}.json`;
      await uploadFile(
        `${uid}/whatnotTransactions/${currentFilename}`,
        JSON.stringify(newTransactions)
      );
      await whatnotProcessFile(
        integration.id,
        "transactions",
        "daily-sync",
        currentFilename
      );
    }
  }

  console.log(
    `[Whatnot] Ledger complete: ${index} batches, ${ledgerDedup.seenOrderUuids.size} unique order uuids, ${ledgerDedup.seenLedgerIds.size} unique ledger ids`
  );
  return ledgerDedup.seenOrderUuids;
}

async function getEarliestInProcessDate(uid: string): Promise<Date | null> {
  const db = firestore();
  const snapshot = await db
    .collection("Sales")
    .where("user", "==", uid)
    .where("in_process", "==", true)
    .where("import_platform", "==", "whatnot")
    .orderBy("sale_date", "asc")
    .limit(1)
    .get();

  if (snapshot.empty) return null;
  const data = snapshot.docs[0].data();
  return data.sale_date?.toDate?.() || null;
}

function normalizeStatus(value: unknown): string {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function isCancelledOrder(myOrder: any): boolean {
  const orderStatus = normalizeStatus(myOrder?.status);
  const prettyStatus = normalizeStatus(myOrder?.prettyStatus);
  const itemStatuses: string[] =
    myOrder?.items?.edges?.map((edge) => normalizeStatus(edge?.node?.status)) ||
    [];

  if (orderStatus === "cancelled") return true;
  if (prettyStatus === "cancelled") return true;
  if (itemStatuses.some((status) => status === "cancelled")) return true;
  return false;
}

function isCompletedOrder(myOrder: any): boolean {
  const orderStatus = normalizeStatus(myOrder?.status);
  const prettyStatus = normalizeStatus(myOrder?.prettyStatus);
  return orderStatus === "completed" || prettyStatus === "completed";
}

const EARNINGS_AVAILABLE_PAYOUT_RE =
  /Earnings became available for payout on (.+)\./i;

const MONTH_INDEX: Record<string, number> = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

/** Matches backend parsePostedDateFromSellerReceipt (noon in user timezone). */
function resolvePostedDateFromMyOrder(
  myOrder: any,
  timeZone: string
): Date | undefined {
  const edges = myOrder?.items?.edges || [];
  for (const edge of edges) {
    const description = edge?.node?.sellerReceipt?.description;
    if (!description) continue;
    const match = String(description).match(EARNINGS_AVAILABLE_PAYOUT_RE);
    if (!match) continue;
    const parts = match[1].trim().match(/^([A-Za-z]+)\s+(\d{1,2}),\s+(\d{4})$/);
    if (!parts) continue;
    const month = MONTH_INDEX[parts[1].slice(0, 3).toLowerCase()];
    if (month == null) continue;
    return zonedDateTimeToUtcDate(
      {
        year: parseInt(parts[3], 10),
        month: month + 1,
        day: parseInt(parts[2], 10),
        hour: 12,
        minute: 0,
        second: 0,
        millisecond: 0,
      },
      timeZone
    );
  }
  return undefined;
}

async function syncInProcess(
  client: WhatnotClient,
  skipTransactionIds: Set<string> = new Set(),
  timeZone: string = "UTC",
  lock: SyncLock
): Promise<void> {
  const uid = await getUserId();
  const db = firestore();
  const snapshot = await db
    .collection("Sales")
    .where("user", "==", uid)
    .where("in_process", "==", true)
    .where("import_platform", "==", "whatnot")
    .get();

  if (snapshot.empty) {
    console.log(`[Whatnot] syncInProcess: no in-process sales found`);
    return;
  }

  let skipped = 0;
  let updated = 0;
  let cancelled = 0;
  for (const doc of snapshot.docs) {
    lock.throwIfAborted();
    const data = doc.data();
    if (!data.transaction_id) continue;

    if (skipTransactionIds.has(data.transaction_id)) {
      skipped++;
      continue;
    }

    try {
      const myOrder = await client.myOrder(data.transaction_id);
      if (!myOrder) continue;

      if (isCancelledOrder(myOrder)) {
        await moveSaleApi(data as Sale, { allowZeroCostDelete: true });
        cancelled++;
        continue;
      }

      if (isCompletedOrder(myOrder)) {
        const update: {
          in_process: false;
          in_process_date: Date;
          posted_date?: Date;
        } = {
          in_process: false,
          in_process_date: new Date(),
        };
        const postedDate = resolvePostedDateFromMyOrder(myOrder, timeZone);
        if (postedDate) update.posted_date = postedDate;
        await doc.ref.update(update);
        updated++;
        continue;
      }

      console.log(
        `[Whatnot] syncInProcess: order ${data.transaction_id} still in process`
      );
    } catch (error) {
      console.warn(
        `[Whatnot] syncInProcess: myOrder check failed for ${data.transaction_id}`,
        error
      );
    }
  }
  console.log(
    `[Whatnot] syncInProcess: ${snapshot.docs.length} in-process sales, skipped ${skipped} from ledger, updated ${updated}, cancelled ${cancelled}`
  );
}

function syncDaily(integrationId: string) {
  return async (dispatch, getState): Promise<boolean> => {
    const started = await withSyncLock(
      integrationId,
      RunningKeys,
      async (lock) => {
        const db = firestore();
        const start = new Date();
        let clearKeepAlive = () => {};

        try {
          const getIntegration = () => {
            const integration = getWhatnotIntegrations(getState()).find(
              ({ id }) => id === integrationId
            );

            if (!integration)
              throw Error(`Integration not found ${integrationId}`);

            return integration;
          };
          const client = new WhatnotClient();

          const cookieUserId = await client.getUserIdFromCookie();
          if (`${cookieUserId}` !== `${getIntegration().uid}`)
            throw new NotLoggedError();

          const username = await client.getUsername();
          if (username !== getIntegration().username)
            throw new NotLoggedError();

          await updateIntegration(integrationId);

          clearKeepAlive = setKeepAliveInterval(client);

          const uid = await getUserId();

          // Widen ledger fetch window to cover older in-process sales
          const earliestInProcessDate = await getEarliestInProcessDate(uid);

          dispatch(updateSyncStatus({ type: "syncing", step: "transactions" }));
          const orderUuids = await syncTransactions(
            client,
            getIntegration(),
            start,
            earliestInProcessDate,
            lock
          );

          await syncInProcess(
            client,
            orderUuids,
            effectiveUserTimeZoneSelector(getState()),
            lock
          );

          lock.throwIfAborted();
          await saveSyncDate(
            integrationId,
            "whatnotLastSyncDateTransactions",
            start.toISOString()
          );

          lock.throwIfAborted();
          await db.collection("Plaid_Integrations").doc(integrationId).update({
            lastSync: start.toISOString(),
          });
          await updateIntegration(integrationId);
        } finally {
          clearKeepAlive();
        }
      }
    );

    if (!started) {
      notifySyncAlreadyLocked(dispatch, updateSyncStatus, "Whatnot");
    }
    return started;
  };
}

export function whatnotSync(
  integration: WhatnotIntegration,
  dispatcher?: Dispatcher
) {
  return async (dispatch, getState) => {
    const extensionId = checkExtensionOrDispatchError(
      dispatch,
      updateSyncStatus,
      "Whatnot",
      dispatcher,
      RequiredExtensionVersion
    );
    if (!extensionId) return;

    setBeforeUnloadHandler("Whatnot");

    try {
      const started = await dispatch(syncDaily(integration.id));
      if (started) dispatch(updateSyncStatus(undefined));
    } catch (e) {
      if (e instanceof NotLoggedError) console.warn(e);
      else console.error(e);
      let code: ErrorSyncStatusCode = "unknown";
      let message: string = (e as Error).message || (e as Error).toString();

      if (e instanceof NotLoggedError) code = "user-not-logged-in";
      else if (e instanceof UnauthorizedError)
        message =
          "There was an authorization error. Please enter Whatnot, log out and then log in.";

      dispatch(
        updateSyncStatus({
          type: "error",
          message,
          code,
          dispatcher,
        })
      );
    } finally {
      clearBeforeUnloadHandler("Whatnot");
    }
  };
}

export function whatnotSyncWithCheck(
  integrationId: string,
  noTimeCheck = false
) {
  return async (dispatch, getState) => {
    const state = getState();
    // Accountant / support impersonation sessions cannot trigger client-side syncs.
    if (isImpersonationSessionSelector(state)) return;
    const plan = userPlanSelector(state);

    if (plan === "ultimate") {
      const integration = getWhatnotIntegrations(state)?.find(
        ({ id }) => id === integrationId
      );

      if (
        integration &&
        integration.type === "what-not" &&
        integration.sync &&
        !integration.error
      ) {
        const shouldRun =
          noTimeCheck ||
          (!isSupportUserSessionSelector(state) &&
            (await shouldRunDailySync(integration.id, ReSyncInterval)));

        if (shouldRun) {
          await dispatch(
            whatnotSync(integration, noTimeCheck ? "manual" : "auto")
          );
        }
      }
    }
  };
}

export function whatnotSyncSales(
  integrationId: string,
  { start, end }: { start: MonthYear; end: MonthYear }
) {
  return async (
    dispatch,
    getState
  ): Promise<{ success?: boolean; error?: boolean }> => {
    const db = firestore();
    const state = getState();

    if (!isUserAllowedToDirectImport(state, "whatnotSales")) {
      return { error: true };
    }
    const integration = getWhatnotIntegrations(state)?.find(
      ({ id }) => id === integrationId
    );

    if (!integration || integration.type !== "what-not" || integration.error)
      return { error: true };

    const extensionId = checkExtensionOrDispatchError(
      dispatch,
      updateSyncStatus,
      "Whatnot",
      "manual",
      RequiredExtensionVersion
    );
    if (!extensionId) return { error: true };

    const client = new WhatnotClient();
    let cookieUserId;
    try {
      cookieUserId = await client.getUserIdFromCookie();
    } catch (e) {
      console.warn(e);
    }

    if (String(cookieUserId) !== String(integration.uid)) {
      dispatch(
        updateSyncStatus({
          type: "error",
          message: "You must be logged in to Whatnot with the correct user",
          code: "user-not-logged-in",
          dispatcher: "manual",
        })
      );
      return { error: true };
    }

    const username = await client.getUsername();
    if (username !== integration.username) {
      dispatch(
        updateSyncStatus({
          type: "error",
          message: "You must be logged in to Whatnot with the correct user",
          code: "user-not-logged-in",
          dispatcher: "manual",
        })
      );
      return { error: true };
    }
    await updateIntegration(integrationId);

    const runningKeys = ["salesIsRunning" as const];
    const isInitialImport = checkIfItIsInitialImport(state, "whatnotSales");
    setBeforeUnloadHandler("Whatnot");
    let lockBlocked = false;

    try {
      const started = await withSyncLock(
        integrationId,
        runningKeys,
        async (lock) => {
          const clearKeepAlive = setKeepAliveInterval(client);
          try {
            console.time("whatnotSyncSales");
            dispatch(
              updateSyncStatus({
                type: "syncing",
                step: "direct-import-sales",
              })
            );
            const uid = await getUserId();
            const timestamp = Date.now();
            const filename = `${uid}/whatnotSales/${timestamp}_${integrationId}.json`;
            const transactionsFilename = `${uid}/whatnotTransactions/${timestamp}_${integrationId}.json`;
            const upload = db.collection("Uploads").doc();
            await upload.set({
              user: uid,
              type: "sales",
              provider: "whatnot",
              filename,
              n: 0,
              timestamp,
              id: upload.id,
            });
            const uploadReturns = db.collection("Uploads").doc();
            await uploadReturns.set({
              user: uid,
              type: "returns",
              provider: "whatnot",
              filename,
              n: 0,
              timestamp,
              id: uploadReturns.id,
            });
            const uploadTransactions = db.collection("Uploads").doc();
            await uploadTransactions.set({
              user: uid,
              type: "transactions",
              provider: "whatnot",
              filename: transactionsFilename,
              n: 0,
              timestamp,
              id: uploadTransactions.id,
            });

            const tz = effectiveUserTimeZoneSelector(getState());
            const startDate = startOfMonthInTimeZone(
              start.year,
              start.month,
              tz
            );
            const endDate = endOfMonthInTimeZone(end.year, end.month, tz);

            console.log(
              `[Whatnot] Date range: ${startDate.toISOString()} to ${endDate.toISOString()}`
            );

            const importType = isInitialImport
              ? "initial-import"
              : "direct-import";
            const ledgerDedup: LedgerDedupState = {
              seenOrderUuids: new Set<string>(),
              seenLedgerIds: new Set<string>(),
            };

            const transactionsFiles: string[] = [];
            let index = 0;

            console.log(`[Whatnot] Fetching ledger transactions...`);
            for await (const transactions of client.getLedgerOverviewFull({
              startTime: startDate.getTime() / 1000,
              endTime: endDate.getTime() / 1000,
            })) {
              lock.throwIfAborted();
              if (!transactions.length) continue;

              const newTransactions = transactions.filter((edge) =>
                keepLedgerTransaction(edge, ledgerDedup)
              );
              if (!newTransactions.length) continue;

              const currentFilename = `${timestamp}_${integrationId}_${String(
                index++
              ).padStart(2, "0")}.json`;
              transactionsFiles.push(currentFilename);
              console.log(
                `[Whatnot] Transactions batch ${index}: ${newTransactions.length} entries`
              );
              lock.throwIfAborted();
              await uploadFile(
                `${uid}/whatnotTransactions/${currentFilename}`,
                JSON.stringify(newTransactions)
              );
              await whatnotProcessFile(
                integrationId,
                "transactions",
                importType,
                currentFilename,
                {
                  uploadSalesId: upload.id,
                  uploadReturnsId: uploadReturns.id,
                  uploadTransactionsId: uploadTransactions.id,
                }
              );
            }
            await uploadTransactions.update({
              files: transactionsFiles,
            });
            console.log(
              `[Whatnot] Ledger complete: ${index} batches, ${ledgerDedup.seenOrderUuids.size} unique order uuids, ${ledgerDedup.seenLedgerIds.size} unique ledger ids`
            );

            await syncInProcess(
              client,
              ledgerDedup.seenOrderUuids,
              effectiveUserTimeZoneSelector(getState()),
              lock
            );
          } finally {
            console.timeEnd("whatnotSyncSales");
            clearKeepAlive();
          }
        }
      );
      if (!started) {
        lockBlocked = true;
        notifySyncAlreadyLocked(dispatch, updateSyncStatus, "Whatnot");
        return { error: true };
      }
    } catch (e) {
      if (e instanceof NotLoggedError) console.warn(e);
      else console.error(e);
      toast.error(`Error: ${(e as Error).message}`);
      return { error: true };
    } finally {
      if (!lockBlocked) dispatch(updateSyncStatus(undefined));
      clearBeforeUnloadHandler("Whatnot");
    }

    await updateIntegration(integrationId);
    return { success: true };
  };
}
